import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFetch } from './api';
import SettingsHeader from './SettingsHeader';
import DeleteObjectModal from './DeleteObjectModal';

// confirmation toast component
function ConfirmationToast({ show, message }) {
  if (!show) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-success-light border border-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-success" />
      <p className="text-sm font-medium text-success-dark">{message}</p>
    </div>
  );
}

function CreateApiKeyModal({
  isOpen, onClose, onCreate, isLoading,
}) {
  const [readOnly, setReadOnly] = useState(false);

  if (!isOpen) return null;

  const handleCreate = () => {
    onCreate(readOnly);
    setReadOnly(false);
  };

  const handleClose = () => {
    setReadOnly(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="modal-backdrop"
        onClick={handleClose}
        onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
        tabIndex={-1}
        aria-hidden="true"
      />
      <div className="modal w-[400px]">
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-surface-900">Create API Key</h2>
        </div>
        <div className="modal-body">
          <p className="text-sm text-surface-600 mb-4">Choose the access level for this API key.</p>
          <div className="flex items-center gap-0.5 border border-surface-200 rounded-lg p-0.5 bg-surface-50">
            <button
              type="button"
              onClick={() => setReadOnly(false)}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                !readOnly
                  ? 'bg-surface-900 text-white shadow-xs'
                  : 'text-surface-400 hover:text-surface-600'
              }`}
            >
              Full Access
            </button>
            <button
              type="button"
              onClick={() => setReadOnly(true)}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                readOnly
                  ? 'bg-surface-900 text-white shadow-xs'
                  : 'text-surface-400 hover:text-surface-600'
              }`}
            >
              Read Only
            </button>
          </div>
          <p className="text-xs text-surface-400 mt-3">
            {readOnly
              ? 'Read-only keys can only make GET requests.'
              : 'Full access keys can read and write data.'}
          </p>
        </div>
        <div className="modal-footer">
          {isLoading ? (
            <ClipLoader size={20} color="#78716C" />
          ) : (
            <>
              <button type="button" onClick={handleClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleCreate} className="btn btn-accent">
                Create Key
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ApiKeys() {
  const { organization } = useOrganization();
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [isDeletingApiKey, setIsDeletingApiKey] = useState(false);
  const [isGeneratingApiKey, setIsGeneratingApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [showEditNicknameConfirmation, setShowEditNicknameConfirmation] = useState(false);

  const trackstar = useFetch();
  const location = useLocation();

  // open the create-key modal when linked to from the command palette, then
  // strip the param so a refresh doesn't reopen it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('modal') === 'create-api-key') {
      setCreateModalOpen(true);
      params.delete('modal');
      const qs = params.toString();
      window.history.replaceState({}, '', qs ? `${location.pathname}?${qs}` : location.pathname);
    }
  }, [location]);

  const copyToClipboard = (content) => {
    const textArea = document.createElement('textarea');
    textArea.value = content;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    setApiKeyCopied(content);
    setTimeout(() => {
      setApiKeyCopied('');
    }, 1500); // Reset the copied state after 1.5 seconds
  };

  useEffect(() => {
    if (organization) {
      setLoadingApiKeys(true);
      trackstar('internal/api-keys').then((response) => {
        const { data } = response;
        setApiKeys(data);
        setLoadingApiKeys(false);
      });
    }
  }, []);

  const handleCreateApiKey = (readOnly) => {
    setIsGeneratingApiKey(true);
    trackstar('internal/api-keys', 'POST', { read_only: readOnly }).then((response) => {
      setApiKeys((prevApiKeys) => [...prevApiKeys, response]);
      setIsGeneratingApiKey(false);
      setCreateModalOpen(false);
      setShowCreateConfirmation(true);
    });
  };
  const handleCloseDeleteModal = (cancelled) => {
    if (!cancelled) {
      setShowDeleteConfirmation(true);
    }
    setDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const handleDeleteApiKey = (apiKey) => {
    setIsDeletingApiKey(true);
    trackstar(`internal/api-keys/${apiKey}`, 'DELETE').then(() => {
      setApiKeys(
        (prevApiKeys) => prevApiKeys.filter((prevApiKey) => prevApiKey.api_key !== apiKey),
      );
      handleCloseDeleteModal(false);
      setIsDeletingApiKey(false);
    });
  };

  const handleOpenDeleteModal = (row) => {
    setDeleteModalOpen(true);
    setRowToDelete(row);
  };

  const handleEditNickname = (apiKey) => {
    const nickname = prompt('Enter a new nickname for this API key:');
    if (nickname) {
      trackstar(`internal/api-keys/${apiKey}`, 'PATCH', { nickname }).then(() => {
        setApiKeys(
          (prevApiKeys) => prevApiKeys.map((prevApiKey) => {
            if (prevApiKey.api_key === apiKey) {
              return { ...prevApiKey, nickname };
            }
            return prevApiKey;
          }),
        );
        setShowEditNicknameConfirmation(true);
      });
    }
  };

  const columns = [{
    field: 'api_key',
    headerName: 'API Key',
    flex: 1,
    renderCell: (params) => (
      <div className="flex items-center w-full justify-between gap-3">
        <span className="font-mono text-surface-600">{'*'.repeat(10) + params.value.slice(-10)}</span>
        <button
          type="button"
          onClick={() => copyToClipboard(params.row.api_key)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-150 ${
            apiKeyCopied === params.row.api_key
              ? 'bg-success-light text-success-dark'
              : 'bg-surface-900 text-white hover:bg-surface-800'
          }`}
          style={{ position: 'sticky', right: 0 }}
        >
          {apiKeyCopied === params.row.api_key ? 'Copied!' : 'Copy Key'}
        </button>
      </div>
    ),
  }, {
    field: 'nickname',
    headerName: 'Nickname',
    flex: 1,
    renderCell: (params) => (
      <div className="flex items-center justify-between gap-2 group">
        <span className={params.value ? 'text-surface-700' : 'text-surface-400'}>
          {params.value ? params.value : 'No Nickname'}
        </span>
        <button
          type="button"
          onClick={() => handleEditNickname(params.row.api_key)}
          className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-surface-100 transition-all"
        >
          <img src="/edit.png" alt="edit" className="h-4 w-4 opacity-60" />
        </button>
      </div>
    ),
  },
  {
    field: 'read_only',
    headerName: 'Access',
    flex: 0.5,
    renderCell: (params) => (
      <span className={`badge ${params.value ? 'badge-warning' : 'badge-success'}`}>
        {params.value ? 'Read Only' : 'Full Access'}
      </span>
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    flex: 1,
    renderCell: (params) => (
      <span>{new Date(params.value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
    ),
  },
  {
    field: 'delete',
    width: 20,
    headerName: '',
    disableColumnMenu: true,
    filterable: false,
    resizable: false,
    sortable: false,
    headerClassName: 'column-header-no-border',
    renderCell: (params) => (
      <IconButton
        size="small"
        onClick={() => handleOpenDeleteModal(params.row.api_key)}
        className="text-surface-400 hover:text-error-dark hover:bg-error-light"
        sx={{ padding: '6px' }}
      >
        <DeleteOutlineIcon sx={{ fontSize: '18px' }} />
      </IconButton>
    ),
  }];

  // DataGrid styling
  const dataGridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#FAFAF9',
      borderBottom: '1px solid #E7E5E4',
      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      fontWeight: 500,
      fontSize: '0.8125rem',
      color: '#78716C',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell': {
      fontSize: '0.8125rem',
      fontWeight: 400,
      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      color: '#44403C',
      borderBottom: '1px solid #F5F5F4',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: '#FAFAF9',
    },
    borderRadius: '10px',
    overflow: 'hidden',
  };

  return (
    <div className="flex flex-col gap-5">
      <SettingsHeader
        title="API Keys"
        subtitle="Create and manage your API keys"
        actions={(
          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            className="btn btn-accent"
          >
            Create API Key
          </button>
        )}
      />
      <ConfirmationToast show={showDeleteConfirmation} message="API Key successfully deleted!" />
      <ConfirmationToast show={showCreateConfirmation} message="API Key successfully created!" />
      <ConfirmationToast show={showEditNicknameConfirmation} message="Nickname successfully updated!" />
      <div className="card overflow-hidden">
        <DataGridPro
          rows={apiKeys}
          columns={columns}
          getRowId={(row) => row.api_key}
          pageSizeOptions={[]}
          autoHeight
          pagination
          loading={loadingApiKeys || isDeletingApiKey || isGeneratingApiKey}
          rowHeight={48}
          sortModel={[{ field: 'created_at', sort: 'desc' }]}
          sx={dataGridSx}
        />
      </div>
      <CreateApiKeyModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateApiKey}
        isLoading={isGeneratingApiKey}
      />
      <DeleteObjectModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteApiKey}
        isLoading={isDeletingApiKey}
        row={rowToDelete}
        objectType="API Key"
      />
    </div>
  );
}

export default ApiKeys;
